import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  IconEdit,
  IconPlus,
  IconHistoryToggle,
  IconPaw,
} from "@tabler/icons-react";

// Hooks and Services
import { getMemberInfo } from "@/hooks/useMembers";
import usePetCare from "@/hooks/usePetCare";

// Types
import {
  PetCareType,
  PetCare as IPetCare,
} from "@/types/petCare";

// Custom Components
import FormHeader from "@/components/ui/organisms/MHeader";
import { getMemberName } from "@/utils/member";
import { ListItem } from "@/components/ui/atoms/MListItem";
import { formatPrettyDate } from "@/utils/date-utils";
import { useNavigate } from "react-router-dom";
import { appendToPath } from "@/utils/nav-utils";
import MSpacer from "@/components/ui/atoms/MSpacer";
import MLoader from "@/components/ui/atoms/MLoader";
import MEmptyCard from "@/components/ui/organisms/MEmptyCard";
import { MButton } from "@/components/ui/atoms/MButtons";
import styles from "./pet-care.module.css";

const PetCare = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const memberInfo = getMemberInfo();
  const memberId = memberInfo.id;
  const name = getMemberName(memberInfo);

  const { deworming, fleaTreatment, loading, fetchPetCare } =
    usePetCare(memberId);

  // Fetch pet care data on component mount
  useEffect(() => {
    fetchPetCare();
  }, [memberId]);

  const handleAddUpdatePetCare = (type: PetCareType) => {
    navigate(appendToPath(location.pathname, "add-update-schedule"), {
      state: {
        petCareType: type,
        petCare: type == PetCareType.DEWORMING ? deworming : fleaTreatment,
      },
    });
  };

  const handleShowHistory = (type: PetCareType) => {
    navigate(appendToPath(location.pathname, "history"), {
      state: {
        petCareType: type,
      },
    });
  };

  // Get tertiary text (reminder days)
  const getTertiaryText = (reminderDays: number, petCareType: PetCareType) => {
    switch (reminderDays) {
      case 0:
        return t(`petcare.reminder.${petCareType}.day`);
      case 1:
        return t("petcare.reminderDaysSingular");
      case 2:
      case 3:
        return t("petcare.reminderDaysPlural", { reminderDays });
      default:
        return t("petcare.reminderNotSet");
    }
  };

  const getActions = (petCare: IPetCare) => {
    const type = petCare.type;
    return [
      {
        id: "edit",
        label: "Edit",
        icon: <IconEdit size={20} />,
        onClick: () => handleAddUpdatePetCare(type),
        isVisible: true,
      },
      {
        id: "show-history",
        label: "Show History",
        icon: <IconHistoryToggle size={20} />,
        onClick: () => handleShowHistory(type),
        isVisible: true,
      },
    ];
  };

  if (loading) {
    return <MLoader />;
  }
  // Otherwise, show the main dashboard
  return (
    <div className="profile-page-root">
      <FormHeader
        module="petcare"
        memberInfo={memberInfo}
        title={t("petcare.title", { name })}
        description={t("petcare.description", { name })}
        learnMoreContent={t("petcare.learnMoreContent")}
        showGradient={true}
      />
      <MSpacer mt={24} />

      {!deworming && !fleaTreatment ? (
        <MEmptyCard
          icon={<IconPaw />}
          primaryTitle={t("petcare.no-petcare")}
          primaryCTA={{
            text: t("petcare.deworming"),
            onClick: () => handleAddUpdatePetCare(PetCareType.DEWORMING),
          }}
          secondaryCTA={{
            text: t("petcare.fleaTickControl"),
            onClick: () => handleAddUpdatePetCare(PetCareType.FLEA_CONTROL),
            variant: "filled"
          }}
        />
      ) : (
        <div className={styles.careContainer}>
          {/* Deworming Button */}
          {deworming ? (
            <ListItem
              title={t("petcare.deworming")}
              secondaryText={t("petcare.dueOn", { date: formatPrettyDate(deworming.nextDueDate) })}
              tertiaryText={getTertiaryText(
                deworming.reminderDays,
                PetCareType.DEWORMING
              )}
              variant="purple"
              type="3-line"
              actions={getActions(deworming)}
            />
          ) : (
            <MButton
              text={t("petcare.deworming")}
              leftIcon={<IconPlus />}
              variant="outline"
              size="md"
              className="w-full"
              onClick={() => handleAddUpdatePetCare(PetCareType.DEWORMING)}
            />
          )}

          {/* Flea & Tick Control Button */}
          {fleaTreatment ? (
            <ListItem
              title={t("petcare.fleaTickControl")}
              secondaryText={t("petcare.dueOn", { date: formatPrettyDate(fleaTreatment.nextDueDate) })}
              tertiaryText={getTertiaryText(
                fleaTreatment.reminderDays,
                PetCareType.FLEA_CONTROL
              )}
              type="3-line"
              actions={getActions(fleaTreatment)}
              variant="purple"
            />
          ) : (
            <MButton
              text={t("petcare.fleaTickControl")}
              leftIcon={<IconPlus />}
              variant="outline"
              size="md"
              className="w-full"
              onClick={() => handleAddUpdatePetCare(PetCareType.FLEA_CONTROL)}
            />            
          )}
        </div>
      )}
    </div>
  );
};

export default PetCare;
